import React from "react";
import classNames from "classnames";

import {
  STYLES,
  STYLE_GRADIENT,
  STYLE_PROGRESS,
  STYLE_CONFIG,
  TYPES,
  TYPE_INDETERMINATE,
  TYPE_DETERMINATE,
} from "./constants";

import { getStyleByProgress } from "./utils";

import styles from "./Linear.module.scss";

interface IndeterminateProps {
  style: (typeof STYLES)[number];
  stopColor1: string;
  railColor: string;
  className?: string;
  ariaValueText?: string;
}

const Indeterminate: React.FC<IndeterminateProps> = ({
  style,
  stopColor1,
  railColor,
  className,
  ariaValueText,
}) => {
  const indicatorStyles = {
    backgroundColor: stopColor1,
  };
  const railStyles = {
    backgroundColor: railColor,
  };

  const innerClassName = classNames(styles.line, {
    [styles.gradient as string]: style === STYLE_GRADIENT,
  });

  return (
    <div
      className={classNames(styles.root, styles.indeterminate, className)}
      style={railStyles}
      aria-valuetext={ariaValueText || "In progress"}
    >
      <div className={innerClassName} style={indicatorStyles} />
    </div>
  );
};

type DeterminateProps = IndeterminateProps & {
  progress: number;
};

const Determinate: React.FC<DeterminateProps> = ({
  style,
  stopColor1,
  railColor,
  progress,
  className,
}) => {
  const indicatorStyles = {
    width: `${progress}%`,
    backgroundColor: stopColor1,
  };
  const railStyles = {
    backgroundColor: railColor,
  };

  const innerClassName = classNames(styles.line, {
    [styles.gradient as string]: style === STYLE_GRADIENT,
  });

  return (
    <div
      className={classNames(styles.root, styles.determinate, className)}
      style={railStyles}
      role="progressbar"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="progressbar"
    >
      <div className={innerClassName} style={indicatorStyles} />
    </div>
  );
};

const PROGRESS_INDICATOR_TYPE_MAP = {
  [TYPE_INDETERMINATE]: Indeterminate,
  [TYPE_DETERMINATE]: Determinate,
};

export interface LinearProps {
  /**
   * Type of indicator for the loader (indeterminate | determinate)
   */
  type: (typeof TYPES)[number];

  /**
   * Color of the loader (dark | light | gradient)
   */
  style: (typeof STYLES)[number];

  /**
   * Progress bar if the chosen Type is determinate
   */
  progress: number;

  className?: string;

  ariaValueText?: string;
}

export default class Linear extends React.PureComponent<LinearProps> {
  render() {
    const { style, type, progress, className, ariaValueText } = this.props;

    const { stopColor1, railColor } =
      style === STYLE_PROGRESS
        ? getStyleByProgress(progress)
        : STYLE_CONFIG[style];

    const Component = PROGRESS_INDICATOR_TYPE_MAP[type];

    return (
      <Component
        style={style}
        stopColor1={stopColor1}
        railColor={railColor}
        progress={progress}
        className={className}
        ariaValueText={ariaValueText}
      />
    );
  }
}
